import { OperationVariables, QueryHookOptions, QueryResult } from '@apollo/client'
import uniqBy from 'lodash/uniqBy'
import { IAutocompleteBaseOption } from '../types'
import { getAutocompleteOptions } from '../utils'
import { IGetAutocompleteOptionsConfig } from '../utils/getAutocompleteOptions'

type TUseQueryHook<D = any, V = OperationVariables> = (
  baseOptions: QueryHookOptions<D, V>
) => QueryResult<D, V>

export interface IAutocompleteOptionsProps<D = any, M = any> {
  /**
   * Programmatically adds a value to the options list
   * and filters it out when API results produce a duplicate
   * @default null
   * @see https://v4.mui.com/components/autocomplete/#google-maps-place
   */
  value?: M | null
  /**
   * Uses data from graphql and returns an array of options
   * @note Use `getAutocompleteOptions` to destructure the data and create fallbacks
   */
  getOptionsConfig: (
    data: D | undefined
  ) => IGetAutocompleteOptionsConfig<M> | IGetAutocompleteOptionsConfig<M>[]
}

/**
 * Wraps around hook and allows programmatic mapping of GraphQL data
 * @example
 * ```
 * const { array = [] } = useAutocompleteQuery(
 *   useShipmentProgressQuery,
 *   { variables: { id: shipmentId } },
 *   {
 *     getOptionsConfig: data => {
 *       const { shipment } = data || {}
 *       const { stops = [] } = shipment || {}
 *       return {
 *         groupName: 'Stops',
 *         groupIndex: 1,
 *         mapOption: ({ city: text, id }) => ({ id, text })
 *       }
 *     }
 *   }
 * )
 * ```
 */
export const useAutocompleteQuery = <D, V>(
  useQueryHook: TUseQueryHook<D, V>,
  hookProps: QueryHookOptions<D, V>,
  autocompleteProps: IAutocompleteOptionsProps<D>
): QueryResult<D, V> & { options: IAutocompleteBaseOption[] } => {
  const { getOptionsConfig, value = null } = autocompleteProps || {}
  const { data, ...rest } = useQueryHook(hookProps)
  const optionsConfig = getOptionsConfig(data)

  let apiResults: IAutocompleteBaseOption[] = []
  let options: IAutocompleteBaseOption[] = []

  if (Array.isArray(optionsConfig)) {
    for (const config of optionsConfig) {
      apiResults = [...apiResults, ...getAutocompleteOptions(config)]
    }
  } else {
    apiResults = getAutocompleteOptions(optionsConfig)
  }

  /**
   * If there's a value, add it to the options
   */
  if (value) {
    options = [value]
  }

  /**
   * Combine API response and value prop
   */
  if (apiResults) {
    options = uniqBy([...apiResults, ...options], 'id')
  }

  return {
    data,
    options,
    ...rest,
  }
}
